import { useEffect } from 'react';
import { Transaction, useDepositSuccess } from 'react-easyrocket';
import { useLocation } from 'react-router';

type UseAutoDepositSuccessProps = {
  transactions: Transaction[];
  onSuccess?: () => void;
  merchantId?: string;
};

/** Хук который отвечает за автоподтверждение депозита.
 * Технически, просто вызывает запрос при выполнении нескольких условий одновременно.
 * Важно: сейчас хук опирается на стейт навигации. Только если в стейте при редиректе передан
 * merchantId равный id транзакции, то автодепозит будет работать ( при выполнении остальных условий),
 * но наличие merchantId является самым важным и изначальным.
 *
 * Вызывается один раз в компоненте истории транзакции либо в том компоненте, куда ведет DepositSuccessRedirect.
 *
 * Можно передать параметр onSuccess - это функция, которая будет вызвана после успешного автодепозита.
 * Добавлена на случай, если invalidateQueries по какой-то своей причине не срабатывает - можно с помощью
 * этого коллбэка стриггерить запрос на транзакции заново, чтобы обновить их статус
 *
 * Это временная версия хука автодепозита, скопированного из библиотеки намеренно, для того чтобы добавить
 * еще один метод оплаты - contiant
 */
export function useAutoDepositSuccess({
  transactions,
  onSuccess,
  merchantId: merchantIdOuter
}: UseAutoDepositSuccessProps) {
  const { state } = useLocation();
  const merchantId: string | undefined = state?.merchantId || merchantIdOuter;
  const transaction: Transaction | undefined = transactions.find((t) => `${t.id}` === merchantId);

  const { mutateAsync } = useDepositSuccess();

  useEffect(() => {
    (async () => {
      if (!merchantId) return;
      if (!transaction) return;

      const autosubmittedDeposits = JSON.parse(
        localStorage.getItem('autosubmittedDeposits') || '{}'
      );
      const isSupportedMethod = /(noda|holypayd|contiant)/.test(transaction.paymentMethod || '');
      /**
       * Транзакция с merchantId существует И в статусе 8 (пендинг) И метод astropay, volt, noda или holypayd.
       * Добавлена проверка с локалстораджем на случай если эффект сработает несколько раз
       */
      if (!(merchantId in autosubmittedDeposits) && transaction.status === 8 && isSupportedMethod) {
        localStorage.setItem(
          'autosubmittedDeposits',
          JSON.stringify({ ...autosubmittedDeposits, [merchantId]: true })
        );
        await mutateAsync({ merchantId: parseInt(merchantId), fee: transaction.commission });
        onSuccess?.();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId, transaction]);
}
